import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getGetPatientResponsibilityStatusQueryKey,
  getGetUserProfileQueryKey,
  upsertUserProfile,
  useUpdateNotificationPreferences as useUpdateNotificationPreferencesGenerated,
} from "@/api/generated";
import type {
  GenderIdentity,
  Pronoun,
  SexAtBirth,
} from "@/api/generated/models";
import type { UserProfileRequest } from "@/api/generated/models/UserProfileRequest";

export interface UserProfileFormData {
  phoneNumber?: string; // comes from auth provider
  email?: string;
  firstName?: string;
  preferredName?: string;
  sexAssignedAtBirth?: string;
  genderIdentity?: string;
  pronouns?: string;
  lastName?: string;
  dateOfBirth?: string;
  residingState?: string;
  timezone?: string;
  streetAddress?: string;
  streetAddress2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  carrierName?: string;
  bridgePayerId?: string;
  planName?: string;
  memberId?: string;
  groupNumber?: string;
  rxBin?: string;
  rxPcn?: string;
  rxGroup?: string;
  userIsPolicyHolder?: boolean;
  medicareBeneficiaryId?: string;
}

const formatDateForBackend = (mmddyyyy: string): string => {
  const [month, day, year] = mmddyyyy.split("/");
  return `${year}-${month}-${day}T00:00:00Z`;
};

export const normalizeProfileFormData = (
  data: UserProfileFormData,
): UserProfileRequest => {
  // if not explicitly included in UserProfileFormData object omit from UserProfileRequest
  const profileData: UserProfileRequest = {};
  if (data.email !== undefined) profileData.email = data.email || null;
  if (data.firstName !== undefined)
    profileData.first_name = data.firstName || null;
  if (data.lastName !== undefined)
    profileData.last_name = data.lastName || null;
  if (data.preferredName !== undefined)
    profileData.preferred_name = data.preferredName || null;
  if (data.dateOfBirth !== undefined) {
    profileData.date_of_birth = data.dateOfBirth
      ? formatDateForBackend(data.dateOfBirth)
      : null;
  }
  if (data.sexAssignedAtBirth !== undefined) {
    profileData.sex_at_birth = (data.sexAssignedAtBirth as SexAtBirth) || null;
  }
  if (data.genderIdentity !== undefined) {
    profileData.gender_identity =
      (data.genderIdentity as GenderIdentity) || null;
  }
  if (data.pronouns !== undefined) {
    profileData.pronouns = (data.pronouns as Pronoun) || null;
  }
  if (data.residingState !== undefined) {
    profileData.state_of_residence = data.residingState || null;
  }
  if (data.timezone !== undefined) {
    profileData.timezone = data.timezone || null;
  }
  if (
    data.streetAddress !== undefined ||
    data.streetAddress2 !== undefined ||
    data.city !== undefined ||
    data.state !== undefined ||
    data.zipCode !== undefined
  ) {
    profileData.home_address = {
      street_address: data.streetAddress || null,
      street_address_2: data.streetAddress2 || null,
      city: data.city || null,
      state: data.state || null,
      zip_code: data.zipCode || null,
    };
  }
  if (
    data.carrierName !== undefined ||
    data.bridgePayerId !== undefined ||
    data.planName !== undefined ||
    data.memberId !== undefined ||
    data.groupNumber !== undefined ||
    data.rxBin !== undefined ||
    data.rxPcn !== undefined ||
    data.rxGroup !== undefined ||
    data.userIsPolicyHolder !== undefined ||
    data.medicareBeneficiaryId !== undefined
  ) {
    profileData.insurance = {
      carrier_name: data.carrierName || null,
      bridge_payer_id: data.bridgePayerId || null,
      plan_name: data.planName || null,
      member_id: data.memberId || null,
      group_number: data.groupNumber || null,
      rx_bin: data.rxBin || null,
      rx_pcn: data.rxPcn || null,
      rx_group: data.rxGroup || null,
      user_is_policy_holder: data.userIsPolicyHolder ?? null,
      medicare_beneficiary_id: data.medicareBeneficiaryId || null,
    };
  }
  return profileData;
};

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: { data: UserProfileFormData }) => {
      const normalizedData = normalizeProfileFormData(data);
      return await upsertUserProfile(normalizedData);
    },
    mutationKey: [getGetUserProfileQueryKey(), "upsert"],
    onSuccess: (updatedProfile) => {
      // The response is the full profile, so write it straight into the cache
      queryClient.setQueryData(getGetUserProfileQueryKey(), updatedProfile);
      // Profile edit may modify insurance info, invalidating the patient responsibility
      queryClient.resetQueries({
        queryKey: getGetPatientResponsibilityStatusQueryKey(),
        exact: false,
      });
    },
  });
};

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();

  return useUpdateNotificationPreferencesGenerated({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetUserProfileQueryKey(),
        });
      },
    },
  });
};
